import { Menu } from "@headlessui/react";
import clsx from "clsx";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MoreVertical } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useInvalidateBasket } from "@/services/api";

type Props = {
  basketId?: number;
  onBasketDeleted: () => void;
};

export const AddDeviceOverflowMenu: FC<Props> = ({
  basketId,
  onBasketDeleted,
}) => {
  const { t } = useTranslation("portal", { keyPrefix: "add-device" });
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutateAsync: invalidateBasket, isLoading: isPending } =
    useInvalidateBasket();

  const handleCloseDeleteModal = () => {
    if (!isPending) {
      setIsDeleteModalOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (basketId) {
      await invalidateBasket({ basketId });
    }

    onBasketDeleted();
    navigate(portalRoutes.cockpit.base, { replace: true });
  };

  return (
    <>
      <Menu as="div" className="relative">
        <Menu.Button
          className="group relative flex size-8 items-center justify-center rounded-md text-foreground-primary focus:outline-none"
          aria-label={t("overflowMenuLabel")}
          data-testid="add-device-overflow-menu"
        >
          <MoreVertical className="size-4" />
          <FocusRing />
        </Menu.Button>
        <Menu.Items className="absolute right-0 top-9 z-10 flex w-fit flex-col gap-1 rounded-lg bg-surface-primary p-2 shadow-md focus:outline-none">
          <Menu.Item>
            {({ active, close }) => (
              <button
                type="button"
                className={clsx(
                  "w-full whitespace-nowrap rounded-md px-3 py-2.5 text-left text-body-base !font-semibold leading-none text-foreground-primary-inverse",
                  active && "bg-surface-tertiary-inverse",
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  close();
                  setIsDeleteModalOpen(true);
                }}
              >
                {t("deleteBasket.menuItem")}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Menu>

      {isDeleteModalOpen && (
        <Modal
          title={t("deleteBasket.title")}
          onClose={handleCloseDeleteModal}
          hasScroll={false}
          variant="dark"
        >
          <ButtonGroupFlexBox
            className="!mt-0"
            rightAligned
            forceButtonWidth
            primaryButton={
              <Button
                variant="primary"
                inverted
                dataTestid="add-device-delete-basket-confirm"
                loading={isPending}
                onClick={() => void handleConfirmDelete()}
              >
                {t("deleteBasket.confirm")}
              </Button>
            }
            secondaryButton={
              <Button
                variant="secondary"
                inverted
                disabled={isPending}
                onClick={handleCloseDeleteModal}
              >
                {t("deleteBasket.cancel")}
              </Button>
            }
          />
        </Modal>
      )}
    </>
  );
};
